import { useMemo } from "react";
import { Plus } from "lucide-react";
import type { WidgetKind } from "@playbookai/types";
import { WIDGET_DESCRIPTIONS, WIDGET_LABELS } from "@playbookai/widget-schemas";
import { Button } from "../../atoms/Button/Button.js";
import {
  Popover,
  PopoverContent,
  PopoverMenuItem,
  PopoverTrigger,
} from "../../molecules/Popover/Popover.js";
import { getWidget } from "../../widgets/registry.js";
import type {
  DashboardWidgetCategory,
  DashboardWidgetMeta,
} from "../../widgets/types.js";
import { CATEGORY_ORDER } from "./helpers.js";

export interface AddWidgetMenuProps {
  catalog: readonly WidgetKind[];
  placedSingletons: ReadonlySet<WidgetKind>;
  onAdd: (kind: WidgetKind) => void;
}

/** Edit-mode "Add widget" popover: catalogue kinds grouped by category,
 *  filtered so placed singletons drop out while `allowMultiple` kinds stay. */
export function AddWidgetMenu({
  catalog,
  placedSingletons,
  onAdd,
}: AddWidgetMenuProps) {
  const available = useMemo(
    () =>
      catalog
        .map((kind) => ({ kind, meta: getWidget(kind)?.dashboard }))
        .filter(
          (e): e is { kind: WidgetKind; meta: DashboardWidgetMeta } =>
            !!e.meta &&
            (!!e.meta.allowMultiple || !placedSingletons.has(e.kind)),
        ),
    [catalog, placedSingletons],
  );
  const groups = useMemo(() => {
    const map = new Map<DashboardWidgetCategory, typeof available>();
    for (const entry of available) {
      const list = map.get(entry.meta.category) ?? [];
      list.push(entry);
      map.set(entry.meta.category, list);
    }
    return map;
  }, [available]);

  return (
    <Popover align="end">
      <PopoverTrigger>
        <Button size="sm" variant="outline">
          <Plus className="w-3.5 h-3.5" /> Add widget
        </Button>
      </PopoverTrigger>
      <PopoverContent className="min-w-70 max-h-[60vh] overflow-y-auto">
        {available.length === 0 ? (
          <div className="px-3 py-3 text-xs text-pb-text-muted text-center">
            All available widgets are on the dashboard.
          </div>
        ) : (
          CATEGORY_ORDER.filter((c) => (groups.get(c)?.length ?? 0) > 0).map(
            (cat) => (
              <div key={cat}>
                <div className="px-2.5 py-1.5 text-dense-2 uppercase tracking-wider text-pb-text-muted font-semibold">
                  {cat}
                </div>
                {(groups.get(cat) ?? []).map(({ kind, meta }) => (
                  <PopoverMenuItem
                    key={kind}
                    icon={meta.icon}
                    onClick={() => onAdd(kind)}
                  >
                    <span className="block text-sm text-pb-text">
                      {WIDGET_LABELS[kind]}
                    </span>
                    <span className="block text-dense-1 text-pb-text-muted">
                      {WIDGET_DESCRIPTIONS[kind]}
                    </span>
                  </PopoverMenuItem>
                ))}
              </div>
            ),
          )
        )}
      </PopoverContent>
    </Popover>
  );
}
